import { ButtonHook } from 'common/Prose/Toolbar/Types'
import copy from 'copy-to-clipboard'
import { EditorView } from 'prosemirror-view'
import { getLocationUrl } from 'hooks/useHistory'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { computed } from 'mobx'

export function useCopyBlockLink(view: EditorView): Omit<ButtonHook<React.MouseEventHandler<unknown>>, 'active'> {
  const { origin, pathname } = getLocationUrl()

  // Returns the blockId of the first header element found in the selection, going from top to bottom
  const blockId = computed(() => {
    const selection = view.state.selection
    const { from, to } = selection
    let fromElement = view.domAtPos(from).node as HTMLElement
    const toElement = view.domAtPos(to).node as HTMLElement

    // In Safari, 'view.domAtPos(from).node' yields the text content of the element,
    // so we need to fetch its parent element if this is the case before being able to access its closest elements
    if (typeof fromElement.closest === 'undefined') {
      fromElement = fromElement.parentElement as HTMLElement
    }
    const fromElementHeader = fromElement?.closest('h1, h2, h3, h4')

    // if the first element is a header, return its id
    if (fromElementHeader) {
      return fromElementHeader.id
    }

    // if not, iterate on its siblings until we find a header or we come to the last element in the selection
    let nextElement: Element | null = fromElement
    let nextElementHeader: Element | null | undefined = fromElementHeader
    while (nextElement && nextElement !== toElement) {
      nextElement = nextElement?.nextElementSibling || null
      nextElementHeader = nextElement?.closest('h1, h2, h3, h4')

      // return the first element that is header
      if (nextElementHeader) return nextElementHeader.id
    }

    // if no sibling is a header, there is no block id to return and copy
    return null
  }).get()

  const handleClick = () => {
    if (!blockId) return

    copy(`${origin}${pathname}#${blockId}`)
    showToastMessage(`Link to block copied!`, 'success')
  }

  return {
    handleClick,
    disabled: !blockId,
  }
}
